import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { AppPlatform } from "@/types/apps";

/**
 * Tailwind class merger. Use anywhere two class lists need to compose without
 * conflicting utilities winning the wrong way.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatPercent(value: number, fractionDigits = 1): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${value.toFixed(fractionDigits)}%`;
}

// Em-dash placeholder for an absent / unparseable date — mirrors the guarded
// per-component date helpers (dashboard/PlanRoadmap, dashboard/RawSignals,
// CppCrossSourceView) so a null or malformed ISO renders "—" rather than
// leaking "Invalid Date" or the `new Date(null)` epoch ("Jan 1, 1970").
const ABSENT_DATE = "—";

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return ABSENT_DATE;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return ABSENT_DATE;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return ABSENT_DATE;
  const timestamp = new Date(iso).getTime();
  if (Number.isNaN(timestamp)) return ABSENT_DATE;
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Canonical display label for the App.platform vocabulary ("ios" |
 * "android") — ONE definition (ENG-TRUE-DEDUP-UX-POLISH D3 remainder;
 * previously duplicated as a local literal in LoginAppSelectionModal,
 * Sidebar, TreeSelect and Team). NOTE: experiment rows carry a DIFFERENT
 * platform vocabulary ("apple" / "google") — label those via
 * `lib/experimentProjection.platformDisplay`, never this map.
 */
export const APP_PLATFORM_LABEL: Record<AppPlatform, "iOS" | "Android"> = {
  ios: "iOS",
  android: "Android",
};
